import { useTranslation } from 'react-i18next';
import type { ExerciseKind } from '@features/Session';

/** Демонстрационная карточка превью. Намеренно фиксированная: панель не должна
 *  мигать содержимым, пока юзер перебирает форматы, а слово с примером и
 *  дистракторами показывает все четыре формата целиком. Перевод к английскому
 *  слову — часть самого упражнения, поэтому он не локализуется. */
const SAMPLE = {
  en: 'rudimentary',
  ipa: '/ˌruːdɪˈmentəri/',
  ru: 'элементарный',
  pos: 'adjective',
  simplePre: 'I know ',
  simplePost: ' Spanish.',
  clozePre: 'He has only a ',
  clozePost: ' knowledge of Spanish.',
  options: ['элементарный', 'глубокий', 'сложный', 'современный'],
};

/** Как выглядит упражнение выбранного формата — уменьшенная копия сцены
 *  тренировки. Только вид: ничего не нажимается и никуда не ведёт. */
export function BuilderPreview({ kind }: { kind: ExerciseKind }) {
  const { t } = useTranslation();
  const isEnglishInput = kind === 'reverse' || kind === 'cloze';
  const placeholder = isEnglishInput
    ? t('session.typeWordEn')
    : t('session.typeTranslation');

  return (
    <div className="bx-pv" data-k={kind} aria-hidden>
      <div className="bx-pv-top">
        <span>✦ 4</span>
        <span className="bx-pv-dots">
          <i className="done" />
          <i className="fail" />
          <i className="cur" />
          <i />
          <i />
          <i />
        </span>
        <span>{t('session.finishShort')}</span>
      </div>

      <div className="bx-pv-body">
        <div className="bx-pv-task">
          {t(`session.task.${kind}`)} <span>· {SAMPLE.pos}</span>
        </div>

        {kind === 'input' && (
          <>
            <div className="bx-pv-cloze">
              {SAMPLE.simplePre}
              <span className="bx-pv-target">{SAMPLE.en}</span>
              {SAMPLE.simplePost}
            </div>
            <div className="bx-pv-ipa">{SAMPLE.ipa}</div>
          </>
        )}
        {kind === 'mcq' && (
          <>
            <div className="bx-pv-word">{SAMPLE.en}</div>
            <div className="bx-pv-ipa">{SAMPLE.ipa}</div>
          </>
        )}
        {kind === 'reverse' && (
          <>
            <div className="bx-pv-word bx-pv-word-ru">{SAMPLE.ru}</div>
            <div className="bx-pv-masked">· · · · · · · · · · ·</div>
          </>
        )}
        {kind === 'cloze' && (
          <div className="bx-pv-cloze">
            {SAMPLE.clozePre}
            <span className="bx-pv-blank" />
            {SAMPLE.clozePost}
          </div>
        )}

        {kind === 'mcq' && (
          <div className="bx-pv-mcq">
            {SAMPLE.options.map((o, i) => (
              <span key={o} className="bx-pv-opt">
                <i>{i + 1}</i>
                {o}
              </span>
            ))}
          </div>
        )}
      </div>

      {kind === 'mcq' ? (
        <div className="bx-pv-foot">
          <span className="bx-pv-hint">{t('session.hint.show')}</span>
          <span className="bx-pv-legend">{t('session.legend.mcq')}</span>
        </div>
      ) : (
        <div className="bx-pv-bar">
          <span className="bx-pv-ph">{placeholder}</span>
          <span className="bx-pv-ghost">{t('session.dontKnow')}</span>
          <span className="bx-pv-hint">
            {kind === 'reverse' ? t('session.hint.firstLetter') : t('session.hint.show')}
          </span>
          <span className="bx-pv-send">↵</span>
        </div>
      )}
    </div>
  );
}
